import { useRouter } from '@tanstack/react-router';
import { useFormik } from 'formik';
import { useSnackbar } from 'notistack';
import { AgButton, AgInput, AgModal, AgModalActions, AgModalContent } from 'src/components/ui';
import { requirePresent } from 'src/lib/requirePresent';
import { buildAppHref } from 'src/lib/routing';
import { appLinks } from 'src/routing/appLinks';
import { z } from 'zod';
import { useCreateBusinessEntity } from './hooks/useBusinessEntityWrites';

type AddBusinessEntityModalProps = { handleClose: () => void; openAddEntity: boolean; refetch: () => unknown };

function AddBusinessEntityModal({ handleClose, openAddEntity, refetch }: AddBusinessEntityModalProps) {
  const { enqueueSnackbar } = useSnackbar();
  const router = useRouter();
  const { create, loading } = useCreateBusinessEntity();

  const formik = useFormik({
    initialValues: { name: '' },
    validateOnChange: false,
    validate: (values) => {
      const schema = z.object({ name: z.string().min(1, 'Name is required') });
      const result = schema.safeParse(values);
      if (!result.success) {
        return result.error.flatten().fieldErrors;
      }
    },
    onSubmit: (input, helpers) => {
      create(input).then(({ data }) => {
        const { id } = requirePresent(data, 'createBusinessEntity').createBusinessEntity;
        handleClose();
        enqueueSnackbar('New Business Entity added', {
          variant: 'success',
          action: (
            <AgButton
              variant="ghost"
              className="text-white"
              href={buildAppHref(router, appLinks.settingsEntities({ id }))}
            >
              Go To
            </AgButton>
          ),
        });
        helpers.resetForm();
        refetch();
      });
    },
    // filter out the contacts and places from the form values
    // because they are handled by their respective grids
  });

  return (
    <AgModal open={openAddEntity} onClose={handleClose} title="Enter New Business Entity Name" size="sm">
      <form onSubmit={formik.handleSubmit}>
        <AgModalContent>
          <p className="mb-4 text-neutral-text-secondary">
            After adding this Business Entity, you can configure it with additional options.
          </p>
          <div className="w-full pt-6">
            <AgInput
              autoFocus
              value={formik.values.name}
              onChange={(value) => formik.setFieldValue('name', value)}
              isInvalid={!!formik.errors.name}
              errorMessage={formik.errors.name as string}
            />
          </div>
        </AgModalContent>
        <AgModalActions>
          <AgButton type="submit" isDisabled={loading}>
            Add
          </AgButton>
        </AgModalActions>
      </form>
    </AgModal>
  );
}

export default AddBusinessEntityModal;
